import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Layout from "@/components/Layout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FileText, ChevronRight, Calendar, CheckCircle2, Clock, BookOpen, Users, User, AlertTriangle, Plus } from "lucide-react";
import { format, isPast, parseISO } from "date-fns";
import { toast } from "react-hot-toast";

export default function Assignments({ user }) {
  const navigate = useNavigate();
  const [assignments, setAssignments] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [students, setStudents] = useState([]);
  const [newAssignment, setNewAssignment] = useState({
    title: "",
    description: "",
    scripture_reference: "",
    due_date: "",
    assigned_to: "all"
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [assignmentsRes, submissionsRes] = await Promise.all([
        axios.get(`${API}/assignments`),
        axios.get(`${API}/submissions`)
      ]);

      setAssignments(assignmentsRes.data);
      setSubmissions(submissionsRes.data);

      // Fetch students for assignment dropdown (teachers only)
      if (user.role === 'teacher') {
        const studentsRes = await axios.get(`${API}/users/students`);
        setStudents(studentsRes.data);
      }
    } catch (error) {
      console.error("Error fetching assignments:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAssignment = async (e) => {
    e.preventDefault();
    
    if (!newAssignment.title) {
      toast.error("Please enter a title");
      return;
    }

    try {
      const payload = {
        title: newAssignment.title,
        description: newAssignment.description,
        scripture_reference: newAssignment.scripture_reference || null,
        due_date: newAssignment.due_date || null,
        assigned_to: newAssignment.assigned_to === "all" ? null : newAssignment.assigned_to
      };

      const response = await axios.post(`${API}/assignments`, payload);
      setAssignments(prev => [response.data, ...prev]);
      setNewAssignment({
        title: "",
        description: "",
        scripture_reference: "",
        due_date: "",
        assigned_to: "all"
      });
      setDialogOpen(false);
      toast.success("Assignment created successfully");
    } catch (error) {
      console.error("Error creating assignment:", error);
      toast.error("Failed to create assignment");
    }
  };

  const getSubmissionStatus = (assignmentId) => {
    const submission = submissions.find(s => s.assignment_id === assignmentId);
    if (!submission) return null;
    return submission;
  };

  const isAssignmentPastDue = (assignment) => {
    if (!assignment.due_date) return false;
    return isPast(parseISO(assignment.due_date));
  };

  // Categorize assignments for students
  const categorizeStudentAssignments = () => {
    const notStarted = [];
    const awaitingReview = [];
    const reviewed = [];
    const pastDue = [];

    assignments.forEach(assignment => {
      const submission = getSubmissionStatus(assignment.assignment_id);
      const isPastDue = isAssignmentPastDue(assignment);

      if (!submission) {
        if (isPastDue) {
          pastDue.push(assignment);
        } else {
          notStarted.push(assignment);
        }
      } else if (submission.status === 'submitted') {
        awaitingReview.push({ assignment, submission });
      } else if (submission.status === 'graded') {
        reviewed.push({ assignment, submission });
      }
    });

    return { notStarted, awaitingReview, reviewed, pastDue };
  };

  // Categorize assignments for teachers (based on submissions)
  const categorizeTeacherAssignments = () => {
    // Get assignments with pending submissions
    const assignmentsWithPending = [];
    const assignmentsReviewed = [];
    const assignmentsPastDue = [];
    const assignmentsNoSubmissions = [];

    assignments.forEach(assignment => {
      const assignmentSubmissions = submissions.filter(s => s.assignment_id === assignment.assignment_id);
      const isPastDue = isAssignmentPastDue(assignment);
      
      const pendingSubmissions = assignmentSubmissions.filter(s => s.status === 'submitted');
      const gradedSubmissions = assignmentSubmissions.filter(s => s.status === 'graded');

      if (pendingSubmissions.length > 0) {
        assignmentsWithPending.push({ assignment, pendingCount: pendingSubmissions.length, totalSubmissions: assignmentSubmissions.length });
      } else if (gradedSubmissions.length > 0) {
        assignmentsReviewed.push({ assignment, reviewedCount: gradedSubmissions.length });
      } else if (isPastDue) {
        assignmentsPastDue.push(assignment);
      } else {
        assignmentsNoSubmissions.push(assignment);
      }
    });

    return { assignmentsWithPending, assignmentsReviewed, assignmentsPastDue, assignmentsNoSubmissions };
  };

  const renderAssignmentCard = (assignment, submission = null, isPastDue = false) => {
    const isCompleted = submission?.status === 'graded';
    const isSubmitted = submission?.status === 'submitted';

    return (
      <Card 
        key={assignment.assignment_id}
        className={`cursor-pointer transition-all duration-300 card-hover ${
          isPastDue ? 'border-red-300 bg-red-50/30' : 'border-slate-100 hover:border-amber-500/30'
        }`}
        onClick={() => navigate(`/assignments/${assignment.assignment_id}`)}
      >
        <CardContent className="p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                isPastDue ? 'bg-red-100' :
                isCompleted ? 'bg-green-100' : 
                isSubmitted ? 'bg-amber-100' : 'bg-slate-100'
              }`}>
                {isPastDue && !submission ? (
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                ) : isCompleted ? (
                  <CheckCircle2 className="w-6 h-6 text-green-600" />
                ) : isSubmitted ? (
                  <Clock className="w-6 h-6 text-amber-600" />
                ) : (
                  <FileText className="w-6 h-6 text-slate-600" />
                )}
              </div>
              <div>
                <h3 className={`font-semibold text-lg ${isPastDue && !submission ? 'text-red-900' : 'text-slate-900'}`}>
                  {assignment.title}
                </h3>
                {assignment.scripture_reference && (
                  <p className="text-amber-700 text-sm font-serif flex items-center mt-1">
                    <BookOpen className="w-3 h-3 mr-1" />
                    {assignment.scripture_reference}
                  </p>
                )}
                <p className="text-slate-600 text-sm mt-1 line-clamp-2">
                  {assignment.description}
                </p>
                <div className="flex flex-wrap items-center gap-3 mt-3">
                  {assignment.due_date && (
                    <div className={`flex items-center text-xs ${isPastDue ? 'text-red-600 font-medium' : 'text-slate-500'}`}>
                      <Calendar className="w-3 h-3 mr-1" />
                      {isPastDue ? 'Past Due: ' : 'Due: '}{format(new Date(assignment.due_date), 'MMM d, yyyy')}
                    </div>
                  )}
                  {user.role === 'teacher' && (
                    <Badge variant="outline" className="text-xs">
                      {assignment.assigned_to ? (
                        <><User className="w-3 h-3 mr-1" />Individual</>
                      ) : (
                        <><Users className="w-3 h-3 mr-1" />Whole Class</>
                      )}
                    </Badge>
                  )}
                  {user.role === 'student' && (
                    <Badge 
                      variant={isCompleted ? 'default' : isSubmitted ? 'secondary' : 'outline'}
                      className={isPastDue && !submission ? 'bg-red-100 text-red-800 hover:bg-red-100' : ''}
                    >
                      {isPastDue && !submission ? 'Past Due' : isCompleted ? 'Graded' : isSubmitted ? 'Submitted' : 'Not Started'}
                    </Badge>
                  )}
                </div>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-slate-400" />
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderTeacherAssignmentCard = (assignment, pendingCount = 0, reviewedCount = 0, isPastDue = false) => {
    return (
      <Card 
        key={assignment.assignment_id}
        className={`cursor-pointer transition-all duration-300 card-hover ${
          isPastDue ? 'border-red-300 bg-red-50/30' : 
          pendingCount > 0 ? 'border-amber-300 bg-amber-50/30' : 'border-slate-100 hover:border-amber-500/30'
        }`}
        onClick={() => navigate(`/assignments/${assignment.assignment_id}`)}
      >
        <CardContent className="p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                isPastDue ? 'bg-red-100' :
                pendingCount > 0 ? 'bg-amber-100' : 
                reviewedCount > 0 ? 'bg-green-100' : 'bg-slate-100'
              }`}>
                {isPastDue ? (
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                ) : pendingCount > 0 ? (
                  <Clock className="w-6 h-6 text-amber-600" />
                ) : reviewedCount > 0 ? (
                  <CheckCircle2 className="w-6 h-6 text-green-600" />
                ) : (
                  <FileText className="w-6 h-6 text-slate-600" />
                )}
              </div>
              <div>
                <h3 className={`font-semibold text-lg ${isPastDue ? 'text-red-900' : 'text-slate-900'}`}>
                  {assignment.title}
                </h3>
                {assignment.scripture_reference && (
                  <p className="text-amber-700 text-sm font-serif flex items-center mt-1">
                    <BookOpen className="w-3 h-3 mr-1" />
                    {assignment.scripture_reference}
                  </p>
                )}
                <p className="text-slate-600 text-sm mt-1 line-clamp-2">
                  {assignment.description}
                </p>
                <div className="flex flex-wrap items-center gap-3 mt-3">
                  {assignment.due_date && (
                    <div className={`flex items-center text-xs ${isPastDue ? 'text-red-600 font-medium' : 'text-slate-500'}`}>
                      <Calendar className="w-3 h-3 mr-1" />
                      {isPastDue ? 'Past Due: ' : 'Due: '}{format(new Date(assignment.due_date), 'MMM d, yyyy')}
                    </div>
                  )}
                  <Badge variant="outline" className="text-xs">
                    {assignment.assigned_to ? (
                      <><User className="w-3 h-3 mr-1" />Individual</>
                    ) : (
                      <><Users className="w-3 h-3 mr-1" />Whole Class</>
                    )}
                  </Badge>
                  {pendingCount > 0 && (
                    <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">
                      {pendingCount} awaiting review
                    </Badge>
                  )}
                  {reviewedCount > 0 && pendingCount === 0 && (
                    <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                      {reviewedCount} reviewed
                    </Badge>
                  )}
                </div>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-slate-400" />
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-slate-300 border-t-amber-500 rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  // Student view categories
  const studentCategories = user.role === 'student' ? categorizeStudentAssignments() : null;
  // Teacher view categories
  const teacherCategories = user.role === 'teacher' ? categorizeTeacherAssignments() : null;

  return (
    <Layout user={user}>
      <div data-testid="assignments-page" className="space-y-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-serif text-2xl md:text-3xl font-bold text-slate-900">
              Assignments
            </h1>
            <p className="text-slate-600 mt-1">
              {user.role === 'teacher' ? 'Manage and review student work' : 'View and complete your assignments'}
            </p>
          </div>
          {user.role === 'teacher' && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-slate-800 hover:bg-slate-900 rounded-full">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Assignment
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Create New Assignment</DialogTitle>
                  <DialogDescription>
                    Create a new assignment for your students
                  </DialogDescription>
                </DialogHeader>
                
                <form onSubmit={handleCreateAssignment} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label htmlFor="title">Title *</Label>
                    <Input
                      id="title"
                      placeholder="Assignment title"
                      value={newAssignment.title}
                      onChange={(e) => setNewAssignment(prev => ({ ...prev, title: e.target.value }))}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      placeholder="Describe the assignment..."
                      value={newAssignment.description}
                      onChange={(e) => setNewAssignment(prev => ({ ...prev, description: e.target.value }))}
                      rows={3}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="scripture">Scripture Reference</Label>
                    <Input
                      id="scripture"
                      placeholder="e.g., John 3:16"
                      value={newAssignment.scripture_reference}
                      onChange={(e) => setNewAssignment(prev => ({ ...prev, scripture_reference: e.target.value }))}
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="due_date">Due Date</Label>
                      <Input
                        id="due_date"
                        type="date"
                        value={newAssignment.due_date}
                        onChange={(e) => setNewAssignment(prev => ({ ...prev, due_date: e.target.value }))}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="assigned_to">Assign To</Label>
                      <Select
                        value={newAssignment.assigned_to}
                        onValueChange={(value) => setNewAssignment({...newAssignment, assigned_to: value})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Whole Class" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Whole Class</SelectItem>
                          {students.map(student => (
                            <SelectItem key={student.user_id} value={student.user_id}>
                              {student.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="flex justify-end gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" className="bg-slate-800 hover:bg-slate-900">
                      Create Assignment
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {assignments.length === 0 ? (
          <Card className="border-slate-100">
            <CardContent className="py-16 text-center">
              <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600">No assignments yet</p>
              {user.role === 'teacher' && (
                <Button 
                  variant="link" 
                  className="mt-2"
                  onClick={() => setDialogOpen(true)}
                >
                  Create your first assignment
                </Button>
              )}
            </CardContent>
          </Card>
        ) : user.role === 'student' ? (
          /* Student View - Categorized */
          <div className="space-y-8">
            {/* Past Due */}
            {studentCategories.pastDue.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                  <h2 className="font-semibold text-lg text-red-900">Past Due</h2>
                  <Badge className="ml-2 bg-red-100 text-red-800 hover:bg-red-100">
                    {studentCategories.pastDue.length}
                  </Badge>
                </div>
                <div className="grid gap-4">
                  {studentCategories.pastDue.map(assignment => 
                    renderAssignmentCard(assignment, null, true)
                  )}
                </div>
              </div>
            )}

            {/* Not Started */}
            {studentCategories.notStarted.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <FileText className="w-5 h-5 text-slate-600" />
                  <h2 className="font-semibold text-lg text-slate-900">Not Started</h2>
                  <Badge variant="outline" className="ml-2">
                    {studentCategories.notStarted.length}
                  </Badge>
                </div>
                <div className="grid gap-4">
                  {studentCategories.notStarted.map(assignment => 
                    renderAssignmentCard(assignment)
                  )}
                </div>
              </div>
            )}

            {/* Awaiting Review */}
            {studentCategories.awaitingReview.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Clock className="w-5 h-5 text-amber-600" />
                  <h2 className="font-semibold text-lg text-slate-900">Awaiting Review</h2>
                  <Badge className="ml-2 bg-amber-100 text-amber-800 hover:bg-amber-100">
                    {studentCategories.awaitingReview.length}
                  </Badge>
                </div>
                <div className="grid gap-4">
                  {studentCategories.awaitingReview.map(({ assignment, submission }) => 
                    renderAssignmentCard(assignment, submission)
                  )}
                </div>
              </div>
            )}

            {/* Reviewed/Graded */}
            {studentCategories.reviewed.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  <h2 className="font-semibold text-lg text-slate-900">Reviewed</h2>
                  <Badge className="ml-2 bg-green-100 text-green-800 hover:bg-green-100">
                    {studentCategories.reviewed.length}
                  </Badge>
                </div>
                <div className="grid gap-4">
                  {studentCategories.reviewed.map(({ assignment, submission }) => 
                    renderAssignmentCard(assignment, submission)
                  )}
                </div>
              </div>
            )}
          </div>
        ) : (
          /* Teacher View - Categorized */
          <div className="space-y-8">
            {/* Awaiting Review */}
            {teacherCategories.assignmentsWithPending.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Clock className="w-5 h-5 text-amber-600" />
                  <h2 className="font-semibold text-lg text-slate-900">Awaiting Your Review</h2>
                  <Badge className="ml-2 bg-amber-100 text-amber-800 hover:bg-amber-100">
                    {teacherCategories.assignmentsWithPending.length}
                  </Badge>
                </div>
                <div className="grid gap-4">
                  {teacherCategories.assignmentsWithPending.map(({ assignment, pendingCount }) => 
                    renderTeacherAssignmentCard(assignment, pendingCount, 0, false)
                  )}
                </div>
              </div>
            )}

            {/* Past Due (no submissions) */}
            {teacherCategories.assignmentsPastDue.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                  <h2 className="font-semibold text-lg text-red-900">Past Due</h2>
                  <Badge className="ml-2 bg-red-100 text-red-800 hover:bg-red-100">
                    {teacherCategories.assignmentsPastDue.length}
                  </Badge>
                </div>
                <div className="grid gap-4">
                  {teacherCategories.assignmentsPastDue.map(assignment => 
                    renderTeacherAssignmentCard(assignment, 0, 0, true)
                  )}
                </div>
              </div>
            )}

            {/* No Submissions Yet */}
            {teacherCategories.assignmentsNoSubmissions.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <FileText className="w-5 h-5 text-slate-600" />
                  <h2 className="font-semibold text-lg text-slate-900">No Submissions Yet</h2>
                  <Badge variant="outline" className="ml-2">
                    {teacherCategories.assignmentsNoSubmissions.length}
                  </Badge>
                </div>
                <div className="grid gap-4">
                  {teacherCategories.assignmentsNoSubmissions.map(assignment => 
                    renderTeacherAssignmentCard(assignment, 0, 0, false)
                  )}
                </div>
              </div>
            )}

            {/* Reviewed */}
            {teacherCategories.assignmentsReviewed.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  <h2 className="font-semibold text-lg text-slate-900">All Reviewed</h2>
                  <Badge className="ml-2 bg-green-100 text-green-800 hover:bg-green-100">
                    {teacherCategories.assignmentsReviewed.length}
                  </Badge>
                </div>
                <div className="grid gap-4">
                  {teacherCategories.assignmentsReviewed.map(({ assignment, reviewedCount }) => 
                    renderTeacherAssignmentCard(assignment, 0, reviewedCount, false)
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
